import { CloseOutlined } from "@ant-design/icons";
import { Pause } from "react-feather";

import "./stylesSuspendModal.css";
import { Input, message, Select } from "antd";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { FEATURE_GUARD_ALLOWED_ENVIRONMENTS } from "../../../../../utils/Constants";
import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../../api/ApiRoutes";
import RebetLoader from "../../../../../components/CommonComponents/RebetLoader/RebetLoader";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../../utils/ErrorUtils";
import { handleSuspensionReasonsConfig, isTruthyFlag } from "../../../../../utils/UserUtils";
import { useSelector } from "react-redux";

const SuspendModal = forwardRef(
  ({ handleCancel, data, refreshUserData }, ref) => {
    const { t } = useTranslation();

    // non-state variables
    const token = JSON.parse(localStorage.getItem("token"));
    const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
      token?.AccessToken
    );
    const { appConfig } = useSelector((state) => state.config);
    const suspensionReasonsConfig = appConfig?.suspension_reasons;

    // state variables
    const [isApiRunning, setIsApiRunning] = useState(false);
    const [formData, setFormData] = useState({
      filterSegment: null,
      reason: "",
      user_id: data?.user_id,
    });

    const defaultSuspensionReasons = useMemo(
      () => [
        { value: "Stolen card fraud", label: t("stolenCardFraud") },
        { value: "Friendly fraud", label: t("friendlyFraud") },
        { value: "ATO", label: t("ato") },
        { value: "Multi accounting", label: t("multiAccounting") },
        { value: "Account sharing", label: t("accountSharing") },
        { value: "Account selling", label: t("accountSelling") },
        { value: "Self Exclusion", label: t("selfExclusion") },
      ],
      [t]
    );

    const suspensionReasons =
      handleSuspensionReasonsConfig(suspensionReasonsConfig) || defaultSuspensionReasons;

    const isUserSuspended = isTruthyFlag(data?.suspended);
    const hasSelectReason = Boolean(formData.filterSegment);
    const hasTextReason = Boolean(String(formData.reason ?? "").trim());
    const shouldDisableSaveButton = !hasSelectReason && !hasTextReason;

    useImperativeHandle(ref, () => ({
      clearFormData,
    }));

    const handleFieldChange = (field, value) => {
      setFormData({ ...formData, [field]: value });
    };

    const clearFormData = () => {
      setFormData({
        filterSegment: null,
        reason: "",
        user_id: data?.user_id,
      });
      setIsApiRunning(false);
    };

    const handleSave = async () => {
      setIsApiRunning(true);
      const trimmedReason = formData.reason.trim();
      const params = {
        user_id: formData.user_id,
        is_suspended: isUserSuspended ? "0" : "1",
        reason: formData.filterSegment || trimmedReason,
      };

      try {
        const response = await PrivateAdminPanelInstance?.patch(
          API_ROUTES?.SUSPEND_UNSUSPEND_USER?.replace(
            "{user_id}",
            params?.user_id
          ),
          params
        );

        const optimisticSuspendedValue = params?.is_suspended;
        const optimisticReason =
        optimisticSuspendedValue === "1" ? formData.filterSegment || trimmedReason : "";

        message.success(
          getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
        );
        try {
          await refreshUserData({
            suspended: optimisticSuspendedValue,
            last_suspend_reason: optimisticReason,
          });
        } catch {
          // PATCH already succeeded; do not treat a stale card as a failed save
        }
        handleCancel();
      } catch (error) {
        message.error(
          getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED)
        );
      } finally {
        setIsApiRunning(false);
      }
    };

    return (
      <div className="suspend-modal-container">
        <div className="modal-header">
          <div className="modal-header-title">
            <Pause size={20} />
            <h2 className="modal-title"> {AppStrings.USER_SUSPENSION}</h2>
          </div>
          <CloseOutlined onClick={handleCancel} className="close-icon" />
        </div>

        <div className="divider" />

        <div className="modal-content">
          {
            !isUserSuspended ? (
              <div className="transaction-info-item">
                <span>{AppStrings.REASON}</span>
                <Select
                  value={formData.filterSegment}
                  onChange={(value) => handleFieldChange("filterSegment", value)}
                  options={suspensionReasons}
                  virtual={false}
                />
              </div>
            ) : (
              <div className="transaction-info-item">
                <Input.TextArea
                  type="text"
                  value={formData.reason}
                  maxLength={255}
                  rows={3}
                  onChange={(e) => handleFieldChange("reason", e.target.value)}
                />
                <span className="max-length">{formData.reason.length}/255</span>
              </div>
            )
          }
        </div>

        <div className="divider" />
        <div className="modal-footer">
          <div className="buttons">
            <button className="cancel-button" onClick={handleCancel} disabled={isApiRunning}>
              {AppStrings.CLOSE}
            </button>
            <button className={`save-button ${shouldDisableSaveButton ? "disabled" : ""}`} onClick={handleSave} disabled={isApiRunning || shouldDisableSaveButton}>
              {isApiRunning ? <RebetLoader height={"20px"} width={"20px"} /> : AppStrings.SAVE}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default SuspendModal;
